// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { newGuid } from "@insite/client-framework/Common/StringHelpers";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import ProductSelectorState from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorState";
import { reducers } from "@insite/client-framework/Store/Reducers";
import { ProductAutocompleteItemModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { current, Draft } from "immer";
import { connect } from "react-redux";

const initialProductSelectorState: ProductSelectorState = {
    isSearching: false,
    variantModalIsOpen: false,
    variantSelection: {},
    variantSelectionCompleted: false,
};

interface QuickOrderLineProductSelectorState {
    quickOrderLines: {
        [key: string]: ProductSelectorState;
    };
}

const createInitialState = () => {
    return {
        quickOrderLines: {
            [newGuid()]: { ...initialProductSelectorState },
            [newGuid()]: { ...initialProductSelectorState },
            [newGuid()]: { ...initialProductSelectorState },
            [newGuid()]: { ...initialProductSelectorState },
        },
    };
};

const initialState: QuickOrderLineProductSelectorState = {
    ...createInitialState(),
};

const reducer = {
    "xQuickOrderLine/ProductSelector/BeginSearchProducts": (
        draft: Draft<QuickOrderLineProductSelectorState>,
        action: { lineId: string },
    ) => {
        draft.quickOrderLines[action.lineId].isSearching = true;
        draft.quickOrderLines[action.lineId].searchResults = undefined;
    },
    "xQuickOrderLine/ProductSelector/CompleteSearchProducts": (
        draft: Draft<QuickOrderLineProductSelectorState>,
        action: { lineId: string; result: ProductAutocompleteItemModel[] | null },
    ) => {
        draft.quickOrderLines[action.lineId].isSearching = false;
        draft.quickOrderLines[action.lineId].searchResults = action.result ?? [];
    },
    "xQuickOrderLine/ProductSelector/UpdateOptions": (
        draft: Draft<QuickOrderLineProductSelectorState>,
        action: { lineId: string; result: ProductModel },
    ) => {
        draft.quickOrderLines[action.lineId].searchResults = [
            {
                id: action.result.id,
                image: action.result.mediumImagePath,
                brandName: action.result.brand?.name ?? "",
                erpNumber: action.result.productNumber,
                brandDetailPagePath: "",
                name: action.result.productTitle,
                url: action.result.urlSegment,
                isNameCustomerOverride: false,
                manufacturerItemNumber: action.result.manufacturerItem,
                title: action.result.productTitle,
            } as ProductAutocompleteItemModel,
        ];
    },
    "xQuickOrderLine/ProductSelector/BeginSetProduct": (
        draft: Draft<QuickOrderLineProductSelectorState>,
        action: { lineId: string },
    ) => {
        draft.quickOrderLines[action.lineId].selectedProductInfo = undefined;
        draft.quickOrderLines[action.lineId].errorType = undefined;
    },
    "xQuickOrderLine/ProductSelector/CompleteSetProduct": (
        draft: Draft<QuickOrderLineProductSelectorState>,
        action: { lineId: string; productInfo?: ProductInfo },
    ) => {
        draft.quickOrderLines[action.lineId].selectedProductInfo = action.productInfo;
    },
    "xQuickOrderLine/ProductSelector/SetUnitOfMeasure": (
        draft: Draft<QuickOrderLineProductSelectorState>,
        action: { lineId: string; unitOfMeasure: string },
    ) => {
        if (!draft.quickOrderLines[action.lineId].selectedProductInfo) {
            return;
        }

        draft.quickOrderLines[action.lineId].selectedProductInfo!.unitOfMeasure = action.unitOfMeasure;
    },
    "xQuickOrderLine/ProductSelector/SetErrorType": (
        draft: Draft<QuickOrderLineProductSelectorState>,
        action: { lineId: string; errorType: string },
    ) => {
        draft.quickOrderLines[action.lineId].errorType = action.errorType;
    },
    "xQuickOrderLine/ProductSelector/CloseVariantModal": (
        draft: Draft<QuickOrderLineProductSelectorState>,
        action: { lineId: string },
    ) => {
        draft.quickOrderLines[action.lineId].variantModalIsOpen = false;
        draft.quickOrderLines[action.lineId].variantSelectionCompleted = false;
        draft.quickOrderLines[action.lineId].variantModalProductId = undefined;
        draft.quickOrderLines[action.lineId].selectedVariantProductInfo = undefined;
        draft.quickOrderLines[action.lineId].variantSelection = {};
    },
    "xQuickOrderLine/ProductSelector/OpenVariantModal": (
        draft: Draft<QuickOrderLineProductSelectorState>,
        action: {
            lineId: string;
            productId: string;
        },
    ) => {
        draft.quickOrderLines[action.lineId].variantModalIsOpen = true;
        draft.quickOrderLines[action.lineId].variantModalProductId = action.productId;
        draft.quickOrderLines[action.lineId].variantSelection = {};
    },
    "xQuickOrderLine/ProductSelector/UpdateVariantSelection": (
        draft: Draft<QuickOrderLineProductSelectorState>,
        action: {
            lineId: string;
            variantSelection: SafeDictionary<string>;
            variantSelectionCompleted: boolean;
            productInfo?: ProductInfo;
        },
    ) => {
        draft.quickOrderLines[action.lineId].selectedVariantProductInfo = action.productInfo;
        draft.quickOrderLines[action.lineId].variantSelection = action.variantSelection;
        draft.quickOrderLines[action.lineId].variantSelectionCompleted = action.variantSelectionCompleted;
    },
    "xQuickOrderLine/ProductSelector/RemoveLine": (
        draft: Draft<QuickOrderLineProductSelectorState>,
        action: {
            lineId: string;
        },
    ) => {
        delete draft.quickOrderLines[action.lineId];
    },
    "xQuickOrderLine/ProductSelector/AddMoreRows": (draft: Draft<QuickOrderLineProductSelectorState>) => {
        draft.quickOrderLines[newGuid()] = { ...initialProductSelectorState };
        draft.quickOrderLines[newGuid()] = { ...initialProductSelectorState };
    },
    "xQuickOrderLine/ProductSelector/Reset": (draft: Draft<QuickOrderLineProductSelectorState>) => {
        return { ...createInitialState() };
    },
};

const xQuickOrderLine = createTypedReducerWithImmer(initialState, reducer);

(reducers as any).xQuickOrderLine = xQuickOrderLine;

export type xQuickOrderLineState = {
    xQuickOrderLine: QuickOrderLineProductSelectorState;
} & ApplicationState;
